import { useState, useMemo, useCallback } from "react";

// Client-side sort shared by every v2 table. The v1 screens each reimplement this,
// with subtly different date handling — some compare date strings lexically, which
// only looks right until the month rolls over.
// `initialDir` exists because "ascending" is the wrong first answer for a date column:
// a board of reviews or a list of scans reads newest-first, and every server query here
// already returns them that way. Defaulting to "asc" made the first paint silently
// reverse the order the caller had just fetched.
export function useSort(rows, initialKey, dateKeys = [], initialDir = "asc") {
  const [sort, setSort] = useState({ key: initialKey, dir: initialDir });

  const toggle = useCallback((key) => {
    setSort((s) => {
      if (s.key !== key) return { key, dir: dateKeys.includes(key) ? "desc" : "asc" };
      return { key, dir: s.dir === "asc" ? "desc" : "asc" };
    });
    // dateKeys is a literal in every caller, so it is stable in practice; listing it
    // keeps the lint rule honest without changing behaviour.
  }, [dateKeys]);

  const sorted = useMemo(() => {
    const out = [...(rows || [])];
    out.sort((a, b) => {
      let x = a?.[sort.key];
      let y = b?.[sort.key];
      if (dateKeys.includes(sort.key)) {
        x = new Date(x).getTime() || 0;
        y = new Date(y).getTime() || 0;
      } else if (typeof x === "number" || typeof y === "number") {
        x = Number(x) || 0; y = Number(y) || 0;
      } else {
        x = (x ?? "").toString().toLowerCase();
        y = (y ?? "").toString().toLowerCase();
      }
      if (x < y) return sort.dir === "asc" ? -1 : 1;
      if (x > y) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return out;
  }, [rows, sort, dateKeys]);

  return { sort, toggleSort: toggle, sorted };
}
